import { lerpColor, lightingFor, solarElevation, type Lighting } from '@veriverse/game-logic';
import { getLamps, getWindows, outdoorMask, type TiledMap } from '@veriverse/map-format';
import { type OfficeLocation, TILE_SIZE } from '@veriverse/shared';
import Phaser from 'phaser';
import { shadeRuns, windowStars, type ShadeRun } from './lighting-math.js';
import { NO_WEATHER, type WeatherEffects } from './weather-effects.js';

/** Depths (see `LAYER_DEPTH` in the world scene and `game/depth.ts`). */
const WINDOW_DEPTH = 1.5; // over the wall tiles, under furniture and people
const SHADE_DEPTH = 1500; // over everything in the world, including decor_above
const GLOW_DEPTH = 1600; // lamps brighten the shade
const GLOW_TEXTURE = 'lamp-glow';
const GLOW_TEXTURE_SIZE = 128;
const WINDOW_ALPHA = 0.7;
const GLOW_ALPHA = 0.45;
const GLOW_COLOR = 0xffd27a;
const CLOUD_GREY = 0x7d8697;
const FOG_WHITE = 0xd5dbe3;
/** Extra outdoor shade under full cloud cover, and fog haze at full fog. */
const CLOUD_SHADE_ALPHA = 0.18;
const FOG_ALPHA = 0.5;
/** Lighting changes slowly; recomputing every 10 s is plenty (spec §6.10). */
export const LIGHTING_UPDATE_MS = 10_000;

/**
 * Real-sun day/night (spec §6.10): a shade over the map (strong on `outdoor` tiles, subtle
 * indoors), sky colour in window rectangles, and lamp glows after sunset.
 */
export class LightingLayer {
  private readonly shade: Phaser.GameObjects.Graphics;
  private readonly windowSky: Phaser.GameObjects.Graphics;
  private readonly glows: Phaser.GameObjects.Image[] = [];
  private readonly runs: ShadeRun[];
  private readonly windows: ReturnType<typeof getWindows>;
  private current: Lighting | undefined;
  private weather: WeatherEffects = NO_WEATHER;

  constructor(
    private readonly scene: Phaser.Scene,
    map: TiledMap,
    private readonly office: OfficeLocation,
    private readonly now: () => Date,
    private intensity = 1,
  ) {
    this.runs = shadeRuns(outdoorMask(map), map.width, map.height);
    this.windows = getWindows(map);
    this.shade = scene.add.graphics().setDepth(SHADE_DEPTH);
    this.windowSky = scene.add.graphics().setDepth(WINDOW_DEPTH);
    ensureGlowTexture(scene);
    for (const lamp of getLamps(map)) {
      const glow = scene.add
        .image((lamp.tile.x + 0.5) * TILE_SIZE, (lamp.tile.y + 0.5) * TILE_SIZE, GLOW_TEXTURE)
        .setDepth(GLOW_DEPTH)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setTint(GLOW_COLOR)
        .setAlpha(GLOW_ALPHA)
        .setVisible(false);
      glow.setScale((lamp.radius * 2 * TILE_SIZE) / GLOW_TEXTURE_SIZE);
      this.glows.push(glow);
    }
  }

  /** The lighting last applied (for the HUD and tests). */
  get lighting(): Lighting | undefined {
    return this.current;
  }

  /** Clouds grey the sky and dim outdoors; fog adds a haze (spec §6.11). Redraws at once. */
  setWeather(weather: WeatherEffects): void {
    this.weather = weather;
    this.update();
  }

  /** The accessibility "darkness intensity" (0..1, spec �14). Redraws at once. */
  setIntensity(intensity: number): void {
    if (intensity === this.intensity) return;
    this.intensity = intensity;
    this.update();
  }

  /** Recomputes the sun and redraws. */
  update(): void {
    const elevation = solarElevation(this.now(), this.office);
    const light = lightingFor(elevation, this.intensity);
    this.current = light;

    this.shade.clear();
    for (const run of this.runs) {
      const alpha = run.outdoor ? light.outdoorAlpha : light.indoorAlpha;
      if (alpha <= 0) continue;
      this.shade.fillStyle(run.outdoor ? light.outdoorColor : light.indoorColor, alpha);
      this.shade.fillRect(run.x * TILE_SIZE, run.y * TILE_SIZE, run.width * TILE_SIZE, TILE_SIZE);
    }
    const { cloudiness, fog } = this.weather;
    for (const run of this.runs) {
      if (!run.outdoor) continue;
      const rect = [
        run.x * TILE_SIZE,
        run.y * TILE_SIZE,
        run.width * TILE_SIZE,
        TILE_SIZE,
      ] as const;
      if (cloudiness > 0) {
        this.shade.fillStyle(CLOUD_GREY, cloudiness * CLOUD_SHADE_ALPHA);
        this.shade.fillRect(...rect);
      }
      if (fog > 0) {
        this.shade.fillStyle(FOG_WHITE, fog * FOG_ALPHA * (1 - light.outdoorAlpha));
        this.shade.fillRect(...rect);
      }
    }
    const sky = lerpColor(
      lerpColor(light.skyColor, CLOUD_GREY, cloudiness * 0.6 * (1 - light.outdoorAlpha)),
      FOG_WHITE,
      fog * (1 - light.outdoorAlpha),
    );
    const stars = light.stars * Math.max(0, 1 - cloudiness * 1.6 - fog * 2);

    this.windowSky.clear();
    for (const window of this.windows) {
      const x = window.x * TILE_SIZE;
      const y = window.y * TILE_SIZE;
      const w = window.width * TILE_SIZE;
      const h = window.height * TILE_SIZE;
      this.windowSky.fillStyle(sky, WINDOW_ALPHA);
      this.windowSky.fillRect(x + 2, y + 2, w - 4, h - 4);
      if (stars > 0) {
        this.windowSky.fillStyle(0xffffff, stars);
        const count = Math.max(2, window.width * window.height * 2);
        for (const star of windowStars(window.id, count))
          this.windowSky.fillRect(Math.round(x + star.fx * w), Math.round(y + star.fy * h), 1, 1);
      }
    }

    for (const glow of this.glows) glow.setVisible(light.lampsOn);
  }

  destroy(): void {
    this.shade.destroy();
    this.windowSky.destroy();
    for (const glow of this.glows) glow.destroy();
  }
}

/** A soft white disc fading to transparent, tinted per lamp. */
function ensureGlowTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(GLOW_TEXTURE)) return;
  const canvas = scene.textures.createCanvas(GLOW_TEXTURE, GLOW_TEXTURE_SIZE, GLOW_TEXTURE_SIZE);
  if (!canvas) return;
  const ctx = canvas.getContext();
  const r = GLOW_TEXTURE_SIZE / 2;
  const gradient = ctx.createRadialGradient(r, r, 0, r, r, r);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.5, 'rgba(255,255,255,0.45)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, GLOW_TEXTURE_SIZE, GLOW_TEXTURE_SIZE);
  canvas.refresh();
}
